import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { buildScopeTranslate } from "lib/I18n";
import MCQChoices from "./MCQChoices";
import SaqInput, { DEFAULT_MAX_CHARS, DEFAULT_MIN_CHARS } from "../SaqInput";
import { isMcqKnowledgeCheck, isSaqKnowledgeCheck } from "../utils";

const tQuestion = buildScopeTranslate("views.content.assessment_framework.assessments.pages.question");

// Move focus to the answer control when a new question loads. The aria-live region
// in the screen announces the question number for screen-reader context.
function focusElement(elementRef) {
  const element = elementRef.current;
  if (element && typeof element.focus === "function") {
    element.focus();
  }
}

const AssessmentFrameworkQuestionScreen = ({
  knowledgeCheck = null,
  currentQuestionIndex,
  totalQuestions,
  selectedId = null,
  submitting = false,
  onSelectAnswer,
  onBack,
  onNextOrSubmit,
}) => {
  const elementRef = useRef(null);
  const questionId = knowledgeCheck ? knowledgeCheck.id : null;
  const isFirstQuestion = currentQuestionIndex === 0;
  const isLastQuestion = currentQuestionIndex === totalQuestions - 1;
  const isMcq = isMcqKnowledgeCheck(knowledgeCheck);
  const isSaq = isSaqKnowledgeCheck(knowledgeCheck);
  const choices = knowledgeCheck?.body?.choices || [];
  const minChars = knowledgeCheck?.body?.min_chars ?? DEFAULT_MIN_CHARS;
  const maxChars = knowledgeCheck?.body?.max_chars ?? DEFAULT_MAX_CHARS;
  const progressText = tQuestion("question_x_of_y", {
    number: currentQuestionIndex + 1,
    total: totalQuestions,
  });

  useEffect(() => {
    if (!questionId) {
      return;
    }

    focusElement(elementRef);
  }, [questionId]);

  if (!knowledgeCheck) {
    return null;
  }

  // tds-button styles via aria-disabled / button--disabled but does not set the
  // native button disabled attribute, so React onClick still fires. Guard clicks.
  const answerLength = (selectedId || "").length;
  const isActionDisabled = submitting || (isSaq
    ? answerLength < minChars || answerLength > maxChars
    : !selectedId);

  const onAnswerChange = (event) => {
    onSelectAnswer(event.target.value);
  };

  const handleNextOrSubmit = () => {
    if (isActionDisabled) {
      return;
    }
    onNextOrSubmit();
  };

  return (
    <div className="th-agentforce-assessment__card">
      <div aria-live="polite" className="th-sr-only">
        {progressText}
      </div>

      {!isFirstQuestion && (
        <tds-button
          class="th-agentforce-assessment__card-back"
          variant="base"
          icon-symbol="back"
          onClick={onBack}
          disabled={submitting}>
          <span>{tQuestion("buttons.back")}</span>
        </tds-button>
      )}

      <div className="th-agentforce-assessment__card-question-block">
        <div className="th-agentforce-assessment__card-badge" aria-hidden="true">
          <tds-badge>
            <span>{progressText}</span>
          </tds-badge>
        </div>

        <h2
          className="th-agentforce-assessment__card-question"
          tabIndex="-1">
          {knowledgeCheck.prompt}
        </h2>

        {isMcq && (
          <MCQChoices
            choices={choices}
            questionId={questionId}
            questionPrompt={knowledgeCheck.prompt}
            selectedId={selectedId}
            onChange={onSelectAnswer}
            disabled={submitting}
            firstChoiceRef={elementRef}
          />
        )}
        {isSaq && (
          <SaqInput
            questionId={questionId}
            questionPrompt={knowledgeCheck.prompt}
            value={selectedId || ""}
            onChange={onAnswerChange}
            disabled={submitting}
            minChars={minChars}
            maxChars={maxChars}
            inputRef={elementRef}
          />
        )}
      </div>

      <div className="th-agentforce-assessment__card-footer">
        <tds-button
          variant="brand"
          onClick={handleNextOrSubmit}
          disabled={isActionDisabled}
          spinner={submitting}>
          <span>
            {isLastQuestion ? tQuestion("buttons.submit") : tQuestion("buttons.next")}
          </span>
        </tds-button>
      </div>
    </div>
  );
};

AssessmentFrameworkQuestionScreen.propTypes = {
  knowledgeCheck: PropTypes.shape({
    id: PropTypes.string.isRequired,
    prompt: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    body: PropTypes.shape({
      choices: PropTypes.arrayOf(PropTypes.object),
      min_chars: PropTypes.number,
      max_chars: PropTypes.number,
    }),
  }),
  currentQuestionIndex: PropTypes.number.isRequired,
  totalQuestions: PropTypes.number.isRequired,
  selectedId: PropTypes.string,
  submitting: PropTypes.bool,
  onSelectAnswer: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  onNextOrSubmit: PropTypes.func.isRequired,
};

export default AssessmentFrameworkQuestionScreen;
